import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pause, Play } from 'lucide-react'
import type { StrategyView } from '@/lib/api-types'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { tokenLogo } from '@/lib/default-tokens'
import { Ticker } from './ticker'
import { useToast } from './toast'

export const STATUS_LABEL: Record<StrategyView['status'], string> = {
  scheduled: 'Scheduled',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Stopped',
  failed: 'Failed',
}

/**
 * A status reads from its dot: green running, amber waiting, grey resting, a faded green once completed (John, Nov 1 2026:
 * not the same green as active), red stopped or failed; the chip itself stays neutral (John, Oct 31 2026).
 */
const STATUS_DOT: Record<StrategyView['status'], string> = {
  scheduled: 'bg-warning',
  active: 'bg-accent',
  paused: 'bg-muted',
  completed: 'bg-accent/35',
  cancelled: 'bg-negative',
  failed: 'bg-negative',
}

export function StatusBadge({ status }: { status: StrategyView['status'] }) {
  const text = status === 'failed' ? 'text-negative' : status === 'active' || status === 'completed' ? 'text-text' : 'text-muted'
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-1 text-xs font-medium leading-none ${text}`}>
      <span className={`size-1.5 shrink-0 rounded-full ${STATUS_DOT[status]}`} aria-hidden="true" />
      {STATUS_LABEL[status]}
    </span>
  )
}

export function strategyKind(s: StrategyView) {
  return `${s.kind === 'limit' ? 'Limit' : 'DCA'} ${s.side === 'sell' ? 'Sell' : 'Buy'}`
}

/** plain-text form: ticker, then the order type and side */
export function strategyTitle(s: StrategyView) {
  return `${(s.token.symbol ?? 'token').toUpperCase()} ${strategyKind(s)}`
}

/** the form for toasts and the activity bar: the logo leads the ticker, then the order type and side (John, Oct 31 2026) */
export function StrategyToastTitle({ strategy: s }: { strategy: StrategyView }) {
  return (
    <>
      <Ticker symbol={(s.token.symbol ?? 'token').toUpperCase()} imageUrl={tokenLogo(s.token.address, s.token.imageUrl ?? undefined)} /> {strategyKind(s)}
    </>
  )
}

/** ticker, a thin divider, then the order type and side (John, Oct 30 2026); the logo sits next to it */
export function StrategyTitle({ strategy: s, className = '' }: { strategy: StrategyView; className?: string }) {
  return (
    <span className={`flex min-w-0 items-center gap-2.5 ${className}`}>
      <span className="whitespace-nowrap font-semibold">{(s.token.symbol ?? 'token').toUpperCase()}</span>
      <span className="h-4 w-px shrink-0 bg-line" aria-hidden="true" />
      <span className="whitespace-nowrap font-medium">{strategyKind(s)}</span>
    </span>
  )
}

/** pause or resume from the strategy's row (John, Oct 30 2026); the row stays clickable around it */
export function PauseResumeButton({ strategy: s }: { strategy: StrategyView }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const toast = useToast()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.strategy.list.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.strategy.get.queryKey({ id: s.id }) })
  }
  const pause = useMutation(trpc.strategy.pause.mutationOptions({ onSuccess: invalidate, onError: (e) => toast('error', 'Could not pause', errorMessage(e)) }))
  const resume = useMutation(trpc.strategy.resume.mutationOptions({ onSuccess: invalidate, onError: (e) => toast('error', 'Could not resume', errorMessage(e)) }))
  const canPause = s.status === 'active' || s.status === 'scheduled'
  const canResume = s.status === 'paused' || s.status === 'failed'
  if (!canPause && !canResume) return null
  const busy = pause.isPending || resume.isPending
  return (
    <button
      type="button"
      className="btn btn-ghost !p-1.5 disabled:opacity-50"
      aria-label={canPause ? 'Pause strategy' : 'Resume strategy'}
      title={canPause ? 'Pause' : 'Resume'}
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation()
        if (canPause) pause.mutate({ id: s.id })
        else resume.mutate({ id: s.id })
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {canPause ? <Pause className="size-4" /> : <Play className="size-4" />}
    </button>
  )
}
